import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  CheckCircle2,
  KeyRound,
  MapPinned,
  Pencil,
  Shield,
  UserCog,
  Users,
  UserPlus,
  XCircle
} from 'lucide-react';
import India from '@svg-maps/india';
import { useAuth } from '../contexts/AuthContext';
import {
  OrganizationMember,
  organizationManagementService
} from '../services/organizationManagementService';
import { AccessPermission } from '../types/adminAccess';
import {
  ALL_INDIA_REGION,
  areAllRegionsAssigned,
  expandAssignedRegions,
  normalizeAssignedRegions,
  toggleAssignedRegion
} from '../utils/regionAccess';

const ACCESS_OPTIONS: Array<{ value: AccessPermission; label: string }> = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'users', label: 'User Management' },
  { value: 'emails', label: 'Email Center' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'events', label: 'Event Management' },
  { value: 'videos', label: 'Video Verification' },
  { value: 'connections', label: 'Connection Requests' },
  { value: 'announcements', label: 'Announcements' },
  { value: 'news', label: 'Selection News' },
  { value: 'reports', label: 'Content Reports' },
  { value: 'organization_management', label: 'Organization Management' }
];

const DEFAULT_ACCESS_BY_ROLE: Record<'admin' | 'employee', AccessPermission[]> = {
  admin: ['dashboard', 'users', 'emails', 'attendance', 'events', 'videos', 'connections', 'announcements', 'news', 'reports'],
  employee: ['dashboard', 'emails', 'attendance', 'events', 'videos', 'connections', 'announcements']
};

interface MemberFormState {
  displayName: string;
  email?: string;
  password?: string;
  role: 'admin' | 'employee';
  access: AccessPermission[];
  regions: string[];
}

const MEMBER_COLOR_CLASSES = [
  'owner-color-0',
  'owner-color-1',
  'owner-color-2',
  'owner-color-3',
  'owner-color-4',
  'owner-color-5',
  'owner-color-6',
  'owner-color-7'
] as const;

const formatDate = (value?: any) => {
  if (!value) return 'N/A';
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value?.toDate === 'function') return value.toDate().toLocaleString();
  return new Date(value).toLocaleString();
};

interface IndiaMapLocation {
  id: string;
  name?: string;
  path: string;
}

const INDIA_MAP_LOCATIONS: IndiaMapLocation[] = (India.locations as IndiaMapLocation[])
  .filter((location) => Boolean(location.name));

const getMemberColorClass = (memberId: string) => {
  const hash = memberId.split('').reduce((total, char) => total + char.charCodeAt(0), 0);
  return MEMBER_COLOR_CLASSES[hash % MEMBER_COLOR_CLASSES.length];
};

const isRegionScopedRole = (role: OrganizationMember['role']) => role === 'admin' || role === 'employee';

const IndiaMapSelector: React.FC<{
  selectedRegions: string[];
  onToggleRegion: (region: string) => void;
  members: OrganizationMember[];
  currentMemberId?: string | null;
}> = ({ selectedRegions, onToggleRegion, members, currentMemberId }) => {
  const expandedSelectedRegions = useMemo(() => expandAssignedRegions(selectedRegions), [selectedRegions]);
  const hasAllIndiaAccess = areAllRegionsAssigned(selectedRegions);
  const selectedCount = expandedSelectedRegions.length;
  const regionAssignments = useMemo(() => {
    const assignments = new Map<string, Array<{ uid: string; name: string; colorClass: string }>>();

    members.forEach((member) => {
      if (!isRegionScopedRole(member.role)) {
        return;
      }

      expandAssignedRegions(normalizeAssignedRegions(member.regions || [])).forEach((region) => {
        const owners = assignments.get(region) || [];
        owners.push({
          uid: member.uid,
          name: member.displayName || member.email,
          colorClass: getMemberColorClass(member.uid)
        });
        assignments.set(region, owners);
      });
    });

    return assignments;
  }, [members]);

  const ownerLegend = useMemo(() => (
    members
      .filter((member) => isRegionScopedRole(member.role))
      .map((member) => ({
      uid: member.uid,
      name: member.displayName || member.email,
      colorClass: getMemberColorClass(member.uid)
    }))
  ), [members]);

  const getRegionName = (location: Pick<IndiaMapLocation, 'id' | 'name'>) => location.name || location.id;

  const getRegionClassName = (location: Pick<IndiaMapLocation, 'id' | 'name'>) => {
    const regionName = getRegionName(location);
    const owners = regionAssignments.get(regionName) || [];
    const currentOwner = owners.find((owner) => owner.uid === currentMemberId);
    const otherOwner = owners.find((owner) => owner.uid !== currentMemberId);

    return [
      'india-assignment-map__location',
      expandedSelectedRegions.includes(regionName) ? 'is-selected' : '',
      currentOwner ? `${currentOwner.colorClass} is-owned-current` : '',
      otherOwner ? `${otherOwner.colorClass} is-owned-other` : ''
    ].filter(Boolean).join(' ');
  };

  const getRegionAriaLabel = (location: Pick<IndiaMapLocation, 'id' | 'name'>) => {
    const regionName = getRegionName(location);
    const owners = regionAssignments.get(regionName) || [];
    return owners.length > 0
      ? `${regionName}. Assigned to ${owners.map((owner) => owner.name).join(', ')}`
      : regionName;
  };

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <MapPinned className="h-4 w-4" />
              India Region Map
            </div>
            <p className="text-xs text-slate-500">Use All India for nationwide access or click states to assign specific responsibility.</p>
          </div>
          <span className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            {hasAllIndiaAccess ? 'All India selected' : `${selectedCount} selected`}
          </span>
        </div>

        <div className="mb-3">
          <button
            type="button"
            onClick={() => onToggleRegion(ALL_INDIA_REGION)}
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              hasAllIndiaAccess
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700'
            }`}
          >
            All India / All Regions
          </button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs font-medium uppercase tracking-[0.18em] text-slate-500">India States Map</div>
            <p className="mt-1 text-xs text-slate-500">Click any state or union territory on the map to assign it.</p>
          </div>
          <div className="bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_transparent_40%),linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <svg
                className="india-assignment-map"
                viewBox={India.viewBox}
                role="img"
                aria-label="India states map"
              >
                {INDIA_MAP_LOCATIONS.map((location) => {
                  const regionName = getRegionName(location);
                  return (
                    <path
                      key={location.id}
                      d={location.path}
                      className={getRegionClassName(location)}
                      aria-label={getRegionAriaLabel(location)}
                      role="button"
                      tabIndex={0}
                      onClick={() => onToggleRegion(regionName)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onToggleRegion(regionName);
                        }
                      }}
                    />
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">Assignment Legend</div>
        <div className="flex flex-wrap gap-2">
          {ownerLegend.map((owner) => (
            <div
              key={owner.uid}
              className="inline-flex items-center gap-2 rounded-full border border-white bg-white px-3 py-1 text-xs text-slate-700 shadow-sm"
            >
              <span className={`h-2.5 w-2.5 rounded-full ${owner.colorClass}`} />
              <span>{owner.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {INDIA_MAP_LOCATIONS.map((location) => {
          const regionName = location.name || location.id;
          const isSelected = expandedSelectedRegions.includes(regionName);
          const owners = regionAssignments.get(regionName) || [];
          const currentOwner = owners.find((owner) => owner.uid === currentMemberId);
          const otherOwner = owners.find((owner) => owner.uid !== currentMemberId);
          const chipOwnerClass = currentOwner
            ? `${currentOwner.colorClass} chip-current`
            : otherOwner
              ? `${otherOwner.colorClass} chip-other`
              : '';
          return (
            <button
              key={`chip-${location.id}`}
              type="button"
              onClick={() => onToggleRegion(regionName)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                isSelected
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : chipOwnerClass || 'border-gray-200 bg-white text-gray-600 hover:border-blue-300 hover:text-blue-700'
              }`}
              title={owners.length > 0 ? `Assigned to: ${owners.map((owner) => owner.name).join(', ')}` : regionName}
            >
              {regionName}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const OrganizationManagement: React.FC = () => {
  const { adminProfile } = useAuth();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingMember, setEditingMember] = useState<OrganizationMember | null>(null);
  const [editForm, setEditForm] = useState<MemberFormState>({
    displayName: '',
    role: 'admin',
    access: DEFAULT_ACCESS_BY_ROLE.admin,
    regions: []
  });
  const [form, setForm] = useState<MemberFormState>({
    displayName: '',
    email: '',
    password: '',
    role: 'admin',
    access: DEFAULT_ACCESS_BY_ROLE.admin,
    regions: []
  });

  const stats = useMemo(() => {
    const admins = members.filter((member) => member.role === 'admin').length;
    const employees = members.filter((member) => member.role === 'employee').length;
    const active = members.filter((member) => member.active).length;

    return { total: members.length, admins, employees, active };
  }, [members]);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const records = await organizationManagementService.getOrganizationMembers();
      setMembers(records);
    } catch (serviceError: any) {
      setError(serviceError?.message || 'Failed to load organization members.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const handleRoleChange = (role: 'admin' | 'employee') => {
    setForm((current) => ({
      ...current,
      role,
      access: DEFAULT_ACCESS_BY_ROLE[role]
    }));
  };

  const handleEditRoleChange = (role: 'admin' | 'employee') => {
    setEditForm((current) => ({
      ...current,
      role,
      access: DEFAULT_ACCESS_BY_ROLE[role]
    }));
  };

  const toggleFormAccess = (permission: AccessPermission) => {
    setForm((current) => ({
      ...current,
      access: current.access.includes(permission)
        ? current.access.filter((item) => item !== permission)
        : [...current.access, permission]
    }));
  };

  const toggleEditAccess = (permission: AccessPermission) => {
    setEditForm((current) => ({
      ...current,
      access: current.access.includes(permission)
        ? current.access.filter((item) => item !== permission)
        : [...current.access, permission]
    }));
  };

  const startEditingMember = (member: OrganizationMember) => {
    setEditingMember(member);
    setEditForm({
      displayName: member.displayName,
      role: member.role === 'employee' ? 'employee' : 'admin',
      access: member.access,
      regions: normalizeAssignedRegions(member.regions || [])
    });
    setError(null);
    setSuccess(null);
  };

  const handleCreateMember = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!adminProfile?.uid) {
      setError('Current admin profile is unavailable.');
      return;
    }

    if (!form.displayName.trim() || !form.email?.trim() || !form.password?.trim()) {
      setError('Name, email and temporary password are required.');
      return;
    }

    if ((form.password || '').length < 6) {
      setError('Temporary password must be at least 6 characters.');
      return;
    }

    if (form.access.length === 0) {
      setError('Select at least one access area.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const result = await organizationManagementService.createOrganizationMember({
        email: form.email || '',
        displayName: form.displayName,
        password: form.password || '',
        role: form.role,
        access: form.access,
        regions: normalizeAssignedRegions(form.regions),
        createdByUid: adminProfile.uid
      });

      setSuccess(
        result.provisioningMode === 'upgraded'
          ? 'Existing user found for this email. Their organization role, access and regions were updated.'
          : 'Organization member created. Share the temporary password securely with the new user.'
      );
      setForm({
        displayName: '',
        email: '',
        password: '',
        role: 'admin',
        access: DEFAULT_ACCESS_BY_ROLE.admin,
        regions: []
      });
      await loadMembers();
    } catch (serviceError: any) {
      setError(serviceError?.message || 'Failed to create organization member.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveMemberAccess = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!editingMember) {
      return;
    }

    if (!editForm.displayName.trim()) {
      setError('Display name is required.');
      return;
    }

    if (editForm.access.length === 0) {
      setError('Select at least one access area.');
      return;
    }

    try {
      setSavingEdit(true);
      setError(null);
      setSuccess(null);
      await organizationManagementService.updateOrganizationMember(editingMember.uid, {
        displayName: editForm.displayName.trim(),
        role: editForm.role,
        access: editForm.access,
        regions: normalizeAssignedRegions(editForm.regions)
      });
      setSuccess('Member access and regions updated successfully.');
      setEditingMember(null);
      await loadMembers();
    } catch (serviceError: any) {
      setError(serviceError?.message || 'Failed to update member access.');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleMemberStatus = async (member: OrganizationMember) => {
    try {
      setError(null);
      setSuccess(null);
      await organizationManagementService.updateOrganizationMember(member.uid, {
        active: !member.active
      });
      await loadMembers();
    } catch (serviceError: any) {
      setError(serviceError?.message || 'Failed to update member status.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Organization Management</h1>
        <p className="mt-1 text-gray-600">Create admins and employees, assign access, and define operational states using an India map GUI.</p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700">
          {success}
        </div>
      )}

      {editingMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <form onSubmit={handleSaveMemberAccess} className="space-y-5 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Edit User Access & Regions</h2>
                  <p className="mt-1 text-sm text-gray-500">{editingMember.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingMember(null)}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XCircle className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr,1.1fr]">
                <div className="space-y-5">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Display Name</label>
                    <input
                      type="text"
                      value={editForm.displayName}
                      onChange={(event) => setEditForm((current) => ({ ...current, displayName: event.target.value }))}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Role</label>
                    <div className="grid grid-cols-2 gap-3">
                      {(['admin', 'employee'] as const).map((role) => (
                        <button
                          key={role}
                          type="button"
                          onClick={() => handleEditRoleChange(role)}
                          className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                            editForm.role === role
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium capitalize">{role}</div>
                          <div className="text-xs text-gray-500">
                            {role === 'admin' ? 'Broader control across admin tools' : 'Operational access with limited scope'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Access</label>
                    <div className="grid grid-cols-1 gap-2">
                      {ACCESS_OPTIONS.filter((option) => option.value !== 'organization_management').map((option) => (
                        <label key={option.value} className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={editForm.access.includes(option.value)}
                            onChange={() => toggleEditAccess(option.value)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Assigned Regions</h3>
                    <p className="text-xs text-gray-500">Select the states and territories this member can operate in.</p>
                  </div>
                  <IndiaMapSelector
                    selectedRegions={editForm.regions}
                    members={members}
                    currentMemberId={editingMember.uid}
                    onToggleRegion={(region) =>
                      setEditForm((current) => ({ ...current, regions: toggleAssignedRegion(current.regions, region) }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setEditingMember(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingEdit}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Members</p>
              <p className="text-3xl font-semibold text-gray-900">{stats.total}</p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Admins</p>
              <p className="text-3xl font-semibold text-gray-900">{stats.admins}</p>
            </div>
            <Shield className="h-8 w-8 text-indigo-600" />
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Employees</p>
              <p className="text-3xl font-semibold text-gray-900">{stats.employees}</p>
            </div>
            <UserCog className="h-8 w-8 text-emerald-600" />
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Access</p>
              <p className="text-3xl font-semibold text-gray-900">{stats.active}</p>
            </div>
            <Building2 className="h-8 w-8 text-amber-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[0.95fr,1.05fr]">
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <div className="rounded-full bg-blue-100 p-2">
              <UserPlus className="h-5 w-5 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create Team Member</h2>
              <p className="text-sm text-gray-500">Create the account, assign app access, and pin the states they will manage.</p>
            </div>
          </div>

          <form className="space-y-4" onSubmit={handleCreateMember}>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Full Name</label>
              <input
                type="text"
                value={form.displayName}
                onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Priya Sharma"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="admin@company.com"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Temporary Password</label>
              <input
                type="test"
                value={form.password}
                onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Minimum 6 characters"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Role</label>
              <div className="grid grid-cols-2 gap-3">
                {(['admin', 'employee'] as const).map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => handleRoleChange(role)}
                    className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                      form.role === role
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium capitalize">{role}</div>
                    <div className="text-xs text-gray-500">
                      {role === 'admin' ? 'Broader control across admin tools' : 'Operational access with limited scope'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Access</label>
              <div className="grid grid-cols-1 gap-2">
                {ACCESS_OPTIONS.filter((option) => option.value !== 'organization_management').map((option) => (
                  <label key={option.value} className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={form.access.includes(option.value)}
                      onChange={() => toggleFormAccess(option.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                <MapPinned className="h-4 w-4" />
                Assigned Regions
              </div>
              <IndiaMapSelector
                selectedRegions={form.regions}
                members={members}
                currentMemberId={null}
                onToggleRegion={(region) =>
                  setForm((current) => ({ ...current, regions: toggleAssignedRegion(current.regions, region) }))
                }
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <UserPlus className="h-4 w-4" />
              {submitting ? 'Creating Account...' : 'Create Member'}
            </button>
          </form>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Organization Team</h2>
              <p className="text-sm text-gray-500">Super admins can edit roles, access and state assignments for every managed member.</p>
            </div>
            <button
              type="button"
              onClick={loadMembers}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              Refresh
            </button>
          </div>

          <div className="space-y-4">
            {members.map((member) => (
              <div key={member.uid} className="rounded-xl border border-gray-200 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-gray-900">{member.displayName || member.email}</h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        member.role === 'superAdmin'
                          ? 'bg-amber-100 text-amber-800'
                          : member.role === 'admin'
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {member.role === 'superAdmin' ? 'super admin' : member.role}
                      </span>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        member.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
                      }`}>
                        {member.active ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{member.email}</p>
                    <p className="mt-2 text-xs text-gray-500">Created: {formatDate(member.createdAt)}</p>
                  </div>

                  {member.role !== 'superAdmin' && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => startEditingMember(member)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit Access
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleMemberStatus(member)}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                          member.active
                            ? 'bg-red-50 text-red-700 hover:bg-red-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        {member.active ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        {member.active ? 'Disable Access' : 'Enable Access'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-4 grid gap-4 xl:grid-cols-[0.9fr,1.1fr]">
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                      <KeyRound className="h-4 w-4" />
                      Access Permissions
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {ACCESS_OPTIONS.map((option) => {
                        const enabled = member.role === 'superAdmin' || member.access.includes(option.value);
                        return (
                          <span
                            key={`${member.uid}-${option.value}`}
                            className={`rounded-full border px-3 py-1 text-xs font-medium ${
                              enabled
                                ? 'border-blue-200 bg-blue-50 text-blue-700'
                                : 'border-gray-200 bg-gray-50 text-gray-400'
                            }`}
                          >
                            {option.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
                      <MapPinned className="h-4 w-4" />
                      Assigned Regions
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {member.role === 'superAdmin' ? (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                          All India Access
                        </span>
                      ) : areAllRegionsAssigned(member.regions || []) ? (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                          All India Access
                        </span>
                      ) : (member.regions || []).length > 0 ? (
                        normalizeAssignedRegions(member.regions || []).map((region) => (
                          <span
                            key={`${member.uid}-${region}`}
                            className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700"
                          >
                            {region}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-gray-400">No states assigned yet.</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {members.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center">
                <Users className="mx-auto h-10 w-10 text-gray-400" />
                <p className="mt-3 text-sm text-gray-500">No organization members found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationManagement;
